'use client';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, getTestId } from '../utils';

type BaseTableAttributes = Pick<
  React.TableHTMLAttributes<HTMLTableElement>,
  'id' | 'className' | 'style' | 'role'
>;

const tableVariants = cva('w-full text-left border-collapse', {
  variants: {
    variant: {
      default: '',
      striped: ''
    },
    rounded: {
      true: ''
    }
  },
  defaultVariants: {
    variant: 'default',
    rounded: true
  }
});

type TableVariantProps = VariantProps<typeof tableVariants>;

export interface SimpleTableProps<T = unknown> extends TableVariantProps, BaseTableAttributes {
  headers: React.ReactNode[];
  data: T[];
  renderRow?: (row: T, index: number) => React.ReactNode[];
  footer?: React.ReactNode[];
  NoContentFallback?: React.ReactNode;
  selectedRows?: number[];
  rowClassName?: string;
  cellClassName?: string;
  /**
   * When true, the outer wrapper uses `overflow-x-auto` instead of `overflow-hidden`,
   * enabling horizontal scrolling for wide tables.
   */
  scrollable?: boolean;
  /**
   * Returns an extra className for each data row. Runs after `selectedRows` and
   * `variant` checks — returned class wins over them for the same property.
   */
  getRowClassName?: (row: T, index: number) => string | undefined;
  /** Called when user clicks anywhere on a row, including cell padding areas. */
  onRowClick?: (row: T, index: number) => void;
  /** Indices of rows whose expanded content is currently shown. */
  expandedRows?: number[];
  /**
   * Content rendered in a full-width row underneath an expanded row. Returning `null`
   * or `undefined` renders nothing, even while the row is listed in `expandedRows`.
   */
  renderExpandedRow?: (row: T, index: number) => React.ReactNode;
  /**
   * Always-on detail row under a data row — no expand state, no highlight. Returning `null`
   * or `undefined` renders nothing for that row.
   */
  renderRowDetail?: (row: T, index: number) => React.ReactNode;
  testId?: string;
}

const arrayRowRenderer = (row: unknown[], index: number): React.ReactNode[] =>
  row.map((cell, cellIndex) => (
    <span key={`simple-table-${index}-${cellIndex}`}>{String(cell)}</span>
  ));

const SimpleTableInner = React.forwardRef<HTMLTableElement, SimpleTableProps>(
  (
    {
      headers,
      data,
      renderRow,
      variant,
      rounded = true,
      footer,
      className,
      NoContentFallback,
      selectedRows,
      cellClassName,
      rowClassName,
      scrollable = false,
      getRowClassName,
      onRowClick,
      expandedRows,
      renderExpandedRow,
      renderRowDetail,
      testId,
      id,
      ...props
    },
    ref
  ) => {
    const rowRenderer = renderRow ?? arrayRowRenderer;
    const isEmpty = data.length === 0;

    return (
      // `relative` keeps absolutely positioned content (e.g. `sr-only`) inside the clip, so a wide
      // table cannot widen the page.
      <div
        className={cn(
          'relative border border-border-base',
          scrollable ? 'overflow-hidden overflow-x-auto' : 'overflow-hidden',
          rounded && 'rounded-[12px]'
        )}
      >
        <table
          ref={ref}
          id={id}
          data-testid={getTestId({ testId, id })}
          className={cn(tableVariants({ rounded, variant }), className)}
          {...props}
        >
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className='border-b border-border-base p-sm text-foreground-muted font-normal bg-background-input'
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isEmpty ? (
              <tr>
                <td colSpan={headers.length} className='p-0'>
                  <div className='flex justify-center items-center text-foreground-muted p-md'>
                    {NoContentFallback ?? <p>empty state</p>}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const cells = rowRenderer(row as unknown[], index);
                const isSelected = selectedRows?.includes(index);
                const handleRowClick = onRowClick ? () => onRowClick(row, index) : undefined;
                const extraRowClass = getRowClassName?.(row, index);
                const isExpanded = expandedRows?.includes(index) ?? false;
                const expandedContent = isExpanded ? renderExpandedRow?.(row, index) : null;
                const detail = renderRowDetail?.(row, index);
                const hasDetail = detail != null && detail !== false;
                return (
                  <React.Fragment key={index}>
                    <tr
                      onClick={handleRowClick}
                      aria-expanded={renderExpandedRow ? isExpanded : undefined}
                      className={cn(
                        'transition ease-out duration-600',
                        handleRowClick && 'cursor-pointer',
                        variant === 'striped' && 'odd:bg-background-input',
                        (isSelected || isExpanded) && 'bg-background-muted',
                        rowClassName,
                        extraRowClass
                      )}
                    >
                      {cells.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={cn(
                            'border-b border-b-border-base text-foreground-muted p-sm',
                            // A detail row carries the border, so row and detail read as one unit.
                            hasDetail && 'border-b-0',
                            cellClassName
                          )}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                    {hasDetail && (
                      <tr data-row-detail=''>
                        <td colSpan={headers.length} className='border-b border-b-border-base p-0'>
                          {detail}
                        </td>
                      </tr>
                    )}
                    {expandedContent != null && expandedContent !== false && (
                      <tr>
                        <td
                          colSpan={headers.length}
                          className='border-b border-b-border-base bg-background-input p-0'
                        >
                          {expandedContent}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {footer && (
            <tfoot>
              <tr className='bg-background-input'>
                {footer.map((cell, index) => (
                  <td
                    key={index}
                    className='p-sm font-medium text-foreground-muted border-t border-border-base'
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  }
);

SimpleTableInner.displayName = 'SimpleTable';

/**
 * Static (non-virtualized) data table — sized to its content. Use for small datasets
 * where `Table`'s virtualization adds too much chrome (fixed scroll height, viewport).
 * Same prop shape as `Table` minus `size`/`sticky`, which only apply to the virtualized variant.
 */
export const SimpleTable = SimpleTableInner as <T = unknown>(
  props: SimpleTableProps<T> & React.RefAttributes<HTMLTableElement>
) => React.ReactElement | null;
